import React from 'react';
import { clsx } from 'clsx';

export interface TimelineItem {
  id: string;
  title: string;
  description?: string;
  time?: string;
  icon?: React.ReactNode;
  variant?:
    | 'default'
    | 'primary'
    | 'secondary'
    | 'accent'
    | 'info'
    | 'success'
    | 'warning'
    | 'error';
}

export interface TimelineProps {
  items: TimelineItem[];
  direction?: 'vertical' | 'horizontal';
  position?: 'start' | 'end' | 'alternate';
  className?: string;
}

export const Timeline: React.FC<TimelineProps> = ({
  items,
  direction = 'vertical',
  position = 'start',
  className,
}) => {
  const timelineClasses = clsx(
    'timeline',
    direction === 'vertical' && 'timeline-vertical',
    direction === 'horizontal' && 'timeline-horizontal',
    className
  );

  const getItemClasses = (index: number) => {
    if (position === 'alternate') {
      return index % 2 === 0 ? 'timeline-start' : 'timeline-end';
    }
    return position === 'start' ? 'timeline-start' : 'timeline-end';
  };

  const getBoxClasses = (variant?: TimelineItem['variant']) => {
    const variantClasses = {
      primary: 'bg-primary text-primary-content',
      secondary: 'bg-secondary text-secondary-content',
      accent: 'bg-accent text-accent-content',
      info: 'bg-info text-info-content',
      success: 'bg-success text-success-content',
      warning: 'bg-warning text-warning-content',
      error: 'bg-error text-error-content',
    };

    return variant && variant !== 'default' ? variantClasses[variant] : '';
  };

  return (
    <ul className={timelineClasses}>
      {items.map((item, index) => (
        <li key={item.id}>
          {index !== 0 && <hr />}
          <div className={getItemClasses(index)}>
            {item.time && <time className="font-mono italic">{item.time}</time>}
            <div className={clsx('timeline-box', getBoxClasses(item.variant))}>
              {item.title && <div className="text-lg font-bold">{item.title}</div>}
              {item.description && <div>{item.description}</div>}
            </div>
          </div>
          <div className="timeline-middle">
            {item.icon || (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                className="w-5 h-5"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
                  clipRule="evenodd"
                />
              </svg>
            )}
          </div>
          {index !== items.length - 1 && <hr />}
        </li>
      ))}
    </ul>
  );
};
